/**
 * V2ListAnimationFields — the list's animation settings as a standard style section
 * ("Animations", the last accordion of the Options list style settings).
 *
 * Four standard checkbox-gated fields (StyleFieldRow, same grid and ✓ as every other
 * style field) over the schema 2.17.0 nullable objects on content.options:
 *   item animation        → itemAnimation        { preset, …}   (entrance, per item)
 *   stagger               → itemStagger          { delayPerItem, …}  — locked while item animation is off
 *   selection animation   → selectionAnimation   { preset, …}   (emphasis on select)
 *   deselection animation → deselectionAnimation { preset, …}   (emphasis on deselect)
 *
 * Every field writes its WHOLE object (or null) in ONE onChange: the FlexibleLayout
 * section adapter builds each write from a ref refreshed only on re-render, so a second
 * write in the same handler would overwrite the first. Other keys of a stored object
 * (duration, easing, from…) are kept when the preset or delay changes.
 */

import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { DEFAULT_DELAY_PER_ITEM_MS } from '@modules/screen-view-v2/animations/stagger';
import { StyleFieldRow } from '../../../shared/styleFields/StyleFieldRow';
import type { FieldConfig } from '../../../../types/editorFieldTypes';
import { FIELD_STYLE_GRID_GAP } from '../../../../config/ui';
import { V2_ENTRANCE_PRESET_OPTIONS, V2_EMPHASIS_PRESET_OPTIONS } from '../../../../config/v2AnimationPresetOptions';
import { getStyleInputTestId, getStyleCheckboxTestId } from '../../../../utils/testIdUtils';
import { getValueByPath } from '../../../../utils/v2FieldOperations';
import { LIST_ANIMATION_SECTION_ID } from '../../../../config/v2OptionsListStyleConfig';

export { LIST_ANIMATION_SECTION_ID };

const OPTIONS_BASE = 'content.options';

interface ObjectField {
  /** Path of the nullable object on the step */
  objectPath: string;
  /** Key inside the object the field edits */
  key: 'preset' | 'delayPerItem';
  config: FieldConfig;
}

const ITEM_ANIMATION: ObjectField = {
  objectPath: `${OPTIONS_BASE}.itemAnimation`,
  key: 'preset',
  config: {
    path: `${OPTIONS_BASE}.itemAnimation`,
    label: 'item animation',
    type: 'select',
    options: V2_ENTRANCE_PRESET_OPTIONS,
    defaultValue: 'fadeInUp',
    isNullable: true,
  },
};

const ITEM_STAGGER: ObjectField = {
  objectPath: `${OPTIONS_BASE}.itemStagger`,
  key: 'delayPerItem',
  config: {
    path: `${OPTIONS_BASE}.itemStagger`,
    label: 'stagger',
    type: 'number',
    min: 0,
    max: 1000,
    step: 10,
    defaultValue: DEFAULT_DELAY_PER_ITEM_MS,
    isNullable: true,
  },
};

const SELECTION_ANIMATION: ObjectField = {
  objectPath: `${OPTIONS_BASE}.selectionAnimation`,
  key: 'preset',
  config: {
    path: `${OPTIONS_BASE}.selectionAnimation`,
    label: 'selection animation',
    type: 'select',
    options: V2_EMPHASIS_PRESET_OPTIONS,
    defaultValue: 'pulse',
    isNullable: true,
  },
};

const DESELECTION_ANIMATION: ObjectField = {
  objectPath: `${OPTIONS_BASE}.deselectionAnimation`,
  key: 'preset',
  config: {
    path: `${OPTIONS_BASE}.deselectionAnimation`,
    label: 'deselection animation',
    type: 'select',
    options: V2_EMPHASIS_PRESET_OPTIONS,
    defaultValue: 'shakeX',
    isNullable: true,
  },
};

export interface V2ListAnimationFieldsProps {
  /** The step (V2ListStyleSections passes it as the section values) */
  values: Record<string, unknown>;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
}

export const V2ListAnimationFields: React.FC<V2ListAnimationFieldsProps> = ({ values, onChange, disabled = false }) => {
  const readObject = useCallback(
    (field: ObjectField) => getValueByPath<Record<string, unknown>>(values, field.objectPath) ?? null,
    [values],
  );

  const writeField = useCallback(
    (field: ObjectField, value: unknown) => {
      if (value === null || value === undefined) {
        onChange(field.objectPath, null);
        return;
      }
      onChange(field.objectPath, { ...(readObject(field) ?? {}), [field.key]: value });
    },
    [onChange, readObject],
  );

  const itemAnimationOn = readObject(ITEM_ANIMATION) !== null;

  const renderField = (field: ObjectField, fieldDisabled: boolean) => (
    <StyleFieldRow
      key={field.objectPath}
      config={field.config}
      value={readObject(field)?.[field.key] ?? null}
      onChange={(value) => writeField(field, value)}
      disabled={fieldDisabled}
      inputTestId={getStyleInputTestId(LIST_ANIMATION_SECTION_ID, field.config.label)}
      checkboxTestId={getStyleCheckboxTestId(LIST_ANIMATION_SECTION_ID, field.config.label)}
    />
  );

  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: FIELD_STYLE_GRID_GAP }}
      data-testid={`style-section-${LIST_ANIMATION_SECTION_ID}-fields`}
    >
      {renderField(ITEM_ANIMATION, disabled)}
      {renderField(ITEM_STAGGER, disabled || !itemAnimationOn)}
      {renderField(SELECTION_ANIMATION, disabled)}
      {renderField(DESELECTION_ANIMATION, disabled)}
    </Box>
  );
};

export default V2ListAnimationFields;
